import { useFeatureFlag } from "@common/api/feature-flags"
import {
  partStockGroupPages,
  partStocksInfiniteQuery,
} from "@common/api/part-stock"
import { ensureListData } from "@common/api/utils"
import { Button } from "@common/components/ui/button"
import { PURCHASE_ORDERS } from "@common/flags"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
  useNavigate,
} from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import {
  type Display,
  DisplayOptions,
} from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requirePermission } from "@/lib/require-permission"
import { StockTable } from "./-components/stock-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  HOLDER_COLUMN,
} from "./-lib/columns"
import {
  hasStockFilters,
  NO_STOCK_FILTERS,
  type StockFilterValues,
  useHolderAccess,
  useStockFilterConfigs,
} from "./-lib/filters"
import { GROUPING_LABELS, GROUPINGS, type Grouping } from "./-lib/grouping"
import {
  defaultColumns,
  displayOf,
  type InventoryDisplay,
  type InventorySearch,
  inventorySearchSchema,
  type StockSort,
  stockParams,
  TABS,
  type Tab,
  tabLabel,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/inventory")({
  staticData: { breadcrumb: "Inventory" },
  validateSearch: (search) => inventorySearchSchema.parse(search),
  loaderDeps: ({ search }) => stockParams(search),
  beforeLoad: async ({ context: { queryClient } }) => {
    await requirePermission(queryClient, "part_balance:read", "/")
  },
  loader: ({ context: { queryClient }, deps }) =>
    ensureListData({
      grouped: Boolean(deps.group),
      groups: () =>
        queryClient.ensureQueryData(partStockGroupPages.groups(deps)),
      flat: () =>
        queryClient.ensureInfiniteQueryData(partStocksInfiniteQuery(deps)),
    }),
  component: InventoryPage,
})

function useUpdateSearch(): (patch: Partial<InventorySearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function InventoryPage(): React.ReactElement {
  const search = Route.useSearch()
  const holders = useHolderAccess()
  const display = visibleDisplay(displayOf(search), holders)
  const open = useMatches({
    select: (matches) =>
      matches.some((match) => match.staticData.detailSidebar),
  })
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)

  return (
    <ListPage
      toolbar={
        <Toolbar display={display} hidden={fit.hidden} panelOpen={open} />
      }
      pane={open && !compact ? "split" : "closed"}
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilters />
      <StockTable
        search={search}
        display={display}
        columns={fit.shown}
        onSort={(column) => update(sortSearch(display, column))}
        onClear={() =>
          update({
            ...NO_STOCK_FILTERS,
            search: undefined,
          })
        }
      />
    </ListPage>
  )
}

function Toolbar({
  display,
  hidden,
  panelOpen,
}: {
  display: InventoryDisplay
  hidden: Column[]
  panelOpen: boolean
}): React.ReactElement {
  const {
    tab = "all",
    search = "",
    status,
    manufacturer,
    condition,
    facility,
    work_order,
  } = Route.useSearch()
  const filtered = hasStockFilters({
    status,
    manufacturer,
    condition,
    facility,
    work_order,
  })
  const update = useUpdateSearch()
  const canCreate = useHasPermission("part:create")
  const ordersEnabled = useFeatureFlag(PURCHASE_ORDERS)
  const canReadOrders = useHasPermission("purchase_order:read")
  const canSeeOrders = ordersEnabled && canReadOrders
  const configs = useStockFilterConfigs()
  const holders = useHolderAccess()
  const tabs = panelOpen
    ? TABS.filter((value) => value === "all" || value === tab)
    : TABS

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={tabs}
          tab={tab}
          label={tabLabel}
          onSelect={(next) =>
            update({ tab: next === "all" ? undefined : next })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Part no. or description…"
          />
          <FilterButton active={filtered} configs={configs} onChange={update} />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={(next) => update(groupingSearch(next))}
          >
            <ColumnChips
              options={
                holders
                  ? COLUMNS
                  : COLUMNS.filter((column) => column !== HOLDER_COLUMN)
              }
              labels={COLUMN_LABELS}
              fixed={["number"]}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next, tab))}
            />
          </DisplayOptions>
          {canSeeOrders && (
            <Button asChild size="sm" variant="outline">
              <Link to="/inventory/purchase-orders">Purchase orders</Link>
            </Button>
          )}
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/inventory/new" search={true}>
                New part
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function groupingSearch({
  group,
  subgroup,
}: Display<Grouping>): Partial<InventorySearch> {
  return {
    group: group === "availability" ? undefined : (group ?? "none"),
    subgroup,
  }
}

function sortSearch(
  display: InventoryDisplay,
  column: StockSort,
): Partial<InventorySearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "number" && direction === "asc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function visibleDisplay(
  display: InventoryDisplay,
  holders: boolean,
): InventoryDisplay {
  return holders
    ? display
    : {
        ...display,
        columns: display.columns.filter((column) => column !== HOLDER_COLUMN),
      }
}

function columnsSearch(columns: Column[], tab: Tab): Partial<InventorySearch> {
  const fallback = defaultColumns(tab)
  const defaults =
    columns.length === fallback.length &&
    columns.every((column) => fallback.includes(column))
  return { columns: defaults ? undefined : columns }
}

function ActiveFilters(): React.ReactElement {
  const { status, manufacturer, condition, facility, work_order } =
    Route.useSearch()
  const update = useUpdateSearch()
  const configs = useStockFilterConfigs()
  const values: StockFilterValues = {
    status,
    manufacturer,
    condition,
    facility,
    work_order,
  }

  return (
    <ActiveFilterBar
      active={hasStockFilters(values)}
      configs={configs}
      values={values}
      onChange={update}
      onClear={() => update(NO_STOCK_FILTERS)}
    />
  )
}
